import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,  
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Spin,
  Alert,
  Progress,
  Tooltip,
  Divider
} from 'antd';
import {
  AppstoreOutlined,
  UserOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloudServerOutlined,
  MonitorOutlined,
  CloudOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { api } from '../services/api';

const { Title, Text } = Typography;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [conjuntos, setConjuntos] = useState([]);
  const [metricas, setMetricas] = useState(null);

  // Determinar si es móvil
  const isMobile = window.innerWidth <= 768;

  // Función para cargar datos
  const cargarDatos = async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      
      // Cargar conjuntos
      console.log('🔍 Dashboard URL:', '/api/conjuntos');
const responseConjuntos = await axios.get('/api/conjuntos', {
      headers: { 
        Authorization: `Bearer ${localStorage.getItem('horeb_admin_token')}`
      }
    });
      if (responseConjuntos.data.success) {
        setConjuntos(responseConjuntos.data.data);
      }

      // Datos simulados para métricas (después los conectaremos con la API real)
      setMetricas({
        cpu_usage: 45.2,
        memory_usage: 68.7,
        disk_usage: 32.1,
        uptime: '5d 12h 34m',
        servicios_pm2: [
          {
            name: 'central-api',
            pid: 1234,
            status: 'online',
            uptime: '5d 12h',
            cpu: 12.5,
            memory: 156,
            restarts: 0,
            puerto: 3001
          },
          {
            name: 'balcones-backend',
            pid: 1235,
            status: 'online',
            uptime: '5d 12h',
            cpu: 8.3,
            memory: 234,
            restarts: 1,
            puerto: 3002
          },
          {
            name: 'votaciones-backend',
            pid: 1236,
            status: 'online',
            uptime: '5d 12h',
            cpu: 3.1,
            memory: 89,
            restarts: 0,
            puerto: 3003
          }
        ],
        bases_datos: [
          {
            nombre: 'horeb_central',
            estado: 'conectada',
            conexiones_activas: 3,
            tamaño_mb: 45.2,
            ultima_conexion: new Date().toISOString()
          },
          {
            nombre: 'balcones_dev',
            estado: 'conectada',
            conexiones_activas: 8,
            tamaño_mb: 235.7,
            ultima_conexion: new Date().toISOString()
          }
        ]
      });

      setError(null);
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al conectar con la API');
    } finally {
      setLoading(false);
      if (showRefreshing) setRefreshing(false);
    }
  };

  // Efecto para cargar datos iniciales
  useEffect(() => {
    cargarDatos();
  }, []);

  // Función para refrescar datos
  const handleRefresh = () => {
    cargarDatos(true);
  };

  // Columnas para tabla de conjuntos
  const columnasConjuntos = [
    {
      title: 'Conjunto',
      dataIndex: 'nombre',
      key: 'nombre',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.dominio}.horebapps.com
          </Text>
        </Space>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => (
        <Tag color={estado === 'activo' ? 'green' : estado === 'mantenimiento' ? 'orange' : 'red'}>
          {estado.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Plan',
      dataIndex: 'plan_contratado',
      key: 'plan_contratado',
      render: (plan) => (
        <Tag color={
          plan === 'premium' ? 'gold' :
          plan === 'estandar' ? 'blue' :
          'default'
        }>
          {plan.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Propiedades',
      dataIndex: 'propiedades_total',
      key: 'propiedades_total',
      render: (count) => <Statistic value={count} valueStyle={{ fontSize: '14px' }} />
    },
    {
      title: 'Usuarios',
      dataIndex: 'usuarios_activos',
      key: 'usuarios_activos',
      render: (count) => <Statistic value={count} valueStyle={{ fontSize: '14px' }} />
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (record) => (
        <Space>
          <Tooltip title="Acceder al sistema">
            <Button 
              type="primary" 
              size="small" 
              icon={<LinkOutlined />}
              onClick={() => window.open(`https://${record.dominio}.horebapps.com`, '_blank')}
            >
              Acceder
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  // Columnas para servicios PM2
  const columnasServicios = [
    {
      title: 'Servicio',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Puerto: {record.puerto || 'N/A'}
          </Text>
        </Space>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag 
          color={status === 'online' ? 'green' : status === 'stopped' ? 'red' : 'orange'}
          icon={status === 'online' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
        >
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Uptime',
      dataIndex: 'uptime',
      key: 'uptime'
    },
    {
      title: 'CPU',
      dataIndex: 'cpu',
      key: 'cpu',
      render: (cpu) => `${cpu}%`
    },
    {
      title: 'Memoria',
      dataIndex: 'memory',
      key: 'memory',
      render: (memory) => `${memory} MB`
    },
    {
      title: 'Reinicios',
      dataIndex: 'restarts',
      key: 'restarts'
    }
  ];

  // Columnas para bases de datos
  const columnasBD = [
    {
      title: 'Base de Datos',
      dataIndex: 'nombre',
      key: 'nombre',
      render: (nombre) => (
        <Space>
          <DatabaseOutlined />
          <Text strong>{nombre}</Text>
        </Space>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => (
        <Tag color={estado === 'conectada' ? 'green' : 'red'}>
          {estado.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Conexiones',
      dataIndex: 'conexiones_activas',
      key: 'conexiones_activas'
    },
    {
      title: 'Tamaño',
      dataIndex: 'tamaño_mb',
      key: 'tamaño_mb',
      render: (size) => `${size} MB`
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Cargando dashboard...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '8px' : '0' }}>
      {/* Header con título y botón refresh */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '28px' }}>
            🏢 Dashboard de Control
          </Title>
          <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
            Panel de administración del sistema multitenant HOREB
          </Text>
        </Col>
        <Col>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={refreshing}
            type="primary"
          >
            {isMobile ? '' : 'Actualizar'}
          </Button>
        </Col>
      </Row>

      {/* Alerta de error si hay problemas de API */}
      {error && (
        <Alert
          message="Problema de conectividad"
          description={`${error}. Mostrando datos de ejemplo.`}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Métricas principales */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Conjuntos Activos"
              value={conjuntos.filter(c => c.estado === 'activo').length}
              prefix={<AppstoreOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#3f8600', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Usuarios Totales"
              value={conjuntos.reduce((total, c) => total + c.usuarios_activos, 0)}
              prefix={<UserOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Servicios Online"
              value={metricas?.servicios_pm2.filter(s => s.status === 'online').length}
              suffix={`/ ${metricas?.servicios_pm2.length}`}
              prefix={<CloudServerOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Uptime del Sistema"
              value={metricas?.uptime}
              prefix={<ClockCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Uso de recursos */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={8}>
          <Card title={<><MonitorOutlined /> Uso de CPU</>} size="small" hoverable>
            <Progress 
              percent={Math.round(metricas?.cpu_usage || 0)} 
              status={metricas && metricas.cpu_usage > 80 ? 'exception' : 'normal'}
              format={(percent) => `${percent}%`}
              strokeColor={{
                from: '#108ee9',
                to: '#87d068',
              }}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title={<><DatabaseOutlined /> Uso de Memoria</>} size="small" hoverable>
            <Progress 
              percent={Math.round(metricas?.memory_usage || 0)}
              status={metricas && metricas.memory_usage > 85 ? 'exception' : 'normal'}
              format={(percent) => `${percent}%`}
              strokeColor={{
                from: '#108ee9',
                to: '#87d068',
              }}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title={<><CloudOutlined /> Uso de Disco</>} size="small" hoverable>
            <Progress 
              percent={Math.round(metricas?.disk_usage || 0)}
              status={metricas && metricas.disk_usage > 90 ? 'exception' : 'normal'}
              format={(percent) => `${percent}%`}
              strokeColor={{
                from: '#108ee9',
                to: '#87d068',
              }}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Tablas de información */}
      <Row gutter={[16, 16]}>
        {/* Conjuntos Registrados */}
        <Col xs={24} lg={12}>
          <Card 
            title={<><AppstoreOutlined /> Conjuntos Registrados</>}
            extra={
              <Tag color="blue">
                {conjuntos.length} total{conjuntos.length !== 1 ? 'es' : ''}
              </Tag>
            }
            hoverable
          >
            <Table
              dataSource={conjuntos}
              columns={columnasConjuntos}
              pagination={false}
              size="small"
              scroll={{ x: isMobile ? 'max-content' : undefined }}
            />
          </Card>
        </Col>

        {/* Servicios PM2 */}
        <Col xs={24} lg={12}>
          <Card 
            title={<><CloudServerOutlined /> Estado de Servicios</>}
            extra={
              <Tag color="green">
                {metricas?.servicios_pm2.filter(s => s.status === 'online').length} online
              </Tag>
            }
            hoverable
          >
            <Table
              dataSource={metricas?.servicios_pm2 || []}
              columns={columnasServicios}
              pagination={false}
              size="small"
              scroll={{ x: isMobile ? 'max-content' : undefined }}
            />
          </Card>
        </Col>

        {/* Bases de Datos */}
        <Col xs={24}>
          <Card 
            title={<><DatabaseOutlined /> Estado de Bases de Datos</>}
            extra={
              <Tag color="green">
                {metricas?.bases_datos.filter(bd => bd.estado === 'conectada').length} conectadas
              </Tag>
            }
            hoverable
          >
            <Table
              dataSource={metricas?.bases_datos || []}
              columns={columnasBD}
              pagination={false}
              size="small"
              scroll={{ x: isMobile ? 'max-content' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* Footer del dashboard */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: 32, 
        padding: '16px',
        background: '#fafafa',
        borderRadius: '8px',
        border: '1px solid #f0f0f0'
      }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          📊 Dashboard actualizado automáticamente cada 30 segundos | 
          🔄 Última actualización: {new Date().toLocaleTimeString()}
        </Text>
      </div>
    </div>
  );
};

export default Dashboard;